import React from 'react';
import { Clock, CheckCircle2, User, Sparkles, MessageCircle, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const IndividualWellness = () => {
  const whatsappNumber = "+919088771406";

  const packages = [
    {
      id: 'package-1',
      badge: 'Package 1',
      tag: 'Focused Support',
      title: 'Introductory Wellness Support',
      subtitle: 'Ideal for those seeking immediate stress relief and an introduction to holistic energy healing.',
      duration: '1 Week / 1-2 Sessions',
      suitableFor: 'Individuals new to energy healing or seeking immediate relief from acute stress and fatigue.',
      services: [
        'Initial Energy & Aura Assessment',
        'Focused Pranic Healing Session',
        'Aura & Chakra Cleansing',
        'Guided Relaxation & Meditation',
        'Personalized Daily Wellness Tips'
      ],
      benefits: [
        'Immediate relief from stress & mental exhaustion',
        'Restored balance & renewed daily clarity',
        'Gentle release of energetic blockages'
      ],
      message: 'Hi Lotus Prana, I would like to enquire about Package 1: Introductory Wellness Support.',
      popular: false
    },
    {
      id: 'package-2',
      badge: 'Package 2',
      tag: 'Most Popular',
      title: 'Deeper Structured Wellness Support',
      subtitle: 'A structured 1-month healing journey for deeper emotional balance, stress recovery, and energy alignment.',
      duration: '4 Weeks / 1 Month Structured Journey',
      suitableFor: 'Individuals dealing with persistent stress, emotional distress, burnout, or seeking structured healing.',
      services: [
        'Comprehensive Energy Diagnostics',
        'Deep Pranic Healing Sessions (Weekly)',
        'Stress & Emotional Trauma Release',
        'Customized Guided Meditation Practices',
        'Bi-weekly Energy Alignment & Progress Checks'
      ],
      benefits: [
        'Sustained emotional stability & peace of mind',
        'Deep release of chronic anxiety & tension',
        'Enhanced resilience & balanced vitality'
      ],
      message: 'Hi Lotus Prana, I would like to enquire about Package 2: Deeper Structured Wellness Support.',
      popular: true
    },
    {
      id: 'package-3',
      badge: 'Package 3',
      tag: 'Comprehensive Journey',
      title: 'Premium Wellness Journey',
      subtitle: 'A transformative 3 to 6-month partnership for complete vital overhaul, inner peace, and personal growth.',
      duration: '3 - 6 Months Holistic Transformation',
      suitableFor: 'Individuals committed to long-term personal transformation, holistic health, and high-performance living.',
      services: [
        'Complete Energy & Vitality Overhaul',
        'Advanced Pranic Healing Protocols',
        'One-on-One Personal Mentorship & Guidance',
        'Customized Experiential Wellness Activities',
        'Priority Support & Continuous Progress Tracking'
      ],
      benefits: [
        'Long-term life transformation & emotional mastery',
        'Optimal energetic vitality & inner harmony',
        'Personalized roadmap for lifelong wellness'
      ],
      message: 'Hi Lotus Prana, I would like to enquire about Package 3: Premium Wellness Journey.',
      popular: false
    }
  ];

  const getWaLink = (msg) => {
    return `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${encodeURIComponent(msg)}`;
  };

  return (
    <section id="individual-wellness" className="scroll-mt-24 bg-background px-6 py-20 lg:px-8 lg:py-28">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7 }}
          className="mx-auto max-w-3xl text-center"
        >
          <span className="mb-3 inline-block rounded-full bg-lotus-petal/60 px-4 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-lotus-deep">
            Personal Healing Pathways
          </span>
          <h2 className="font-serif text-3xl leading-tight text-lotus-ink sm:text-4xl lg:text-5xl">
            Individual Wellness Packages
          </h2>
          <p className="mt-4 text-base text-lotus-ink/70 sm:text-lg">
            Choose a tailored wellness journey structured to revitalize your mind, restore your energy, and cultivate lasting inner peace.
          </p>
        </motion.div>

        {/* Package Grid */}
        <div className="mt-16 grid gap-8 lg:grid-cols-3 lg:gap-8">
          {packages.map((pkg, idx) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.7, delay: idx * 0.15 }}
              whileHover={{ y: -6 }}
              className={`relative flex flex-col justify-between rounded-3xl bg-white p-8 transition-all duration-300 hover:shadow-2xl ${
                pkg.popular
                  ? 'border-2 border-lotus-deep shadow-lg ring-4 ring-lotus-deep/10'
                  : 'border border-lotus-petal/80 shadow-sm hover:border-lotus-deep/40'
              }`}
            >
              {/* Popular Badge */}
              {pkg.popular && (
                <motion.div 
                  animate={{ scale: [1, 1.03, 1] }}
                  transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
                  className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-lotus-deep px-4 py-1 text-xs font-medium uppercase tracking-wider text-white shadow-md" style={{minWidth: 'max-content'}}
                >
                  ★ Most Popular Choice
                </motion.div>
              )}

              <div>
                {/* Top Badge & Duration */}
                <div className="flex items-center justify-between border-b border-lotus-petal/60 pb-4">
                  <span className="text-xs font-bold uppercase tracking-wider text-lotus-deep">
                    {pkg.badge}
                  </span>
                  <div className="flex items-center gap-1.5 text-xs font-medium text-lotus-ink/70">
                    <Clock size={14} className="text-lotus-deep" />
                    <span>{pkg.duration}</span>
                  </div>
                </div>

                {/* Package Name */}
                <h3 className="mt-5 font-serif text-2xl font-medium text-lotus-ink leading-snug">
                  {pkg.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-lotus-ink/70">
                  {pkg.subtitle}
                </p>

                {/* Services Included */}
                <div className="mt-6 border-t border-lotus-petal/40 pt-6">
                  <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-lotus-ink/80">
                    <Sparkles size={14} className="text-lotus-deep" />
                    Services Included
                  </h4>
                  <ul className="mt-3 space-y-2.5 text-sm text-lotus-ink">
                    {pkg.services.map((service, sIdx) => (
                      <li key={sIdx} className="flex items-start gap-2.5">
                        <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-lotus-deep" />
                        <span className="text-lotus-ink/90 leading-snug">{service}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Benefits */}
                <div className="mt-6 border-t border-lotus-petal/40 pt-5">
                  <h4 className="text-xs font-semibold uppercase tracking-widest text-lotus-ink/80">
                    Key Benefits
                  </h4>
                  <ul className="mt-2.5 space-y-2 text-xs text-lotus-ink/80">
                    {pkg.benefits.map((benefit, bIdx) => (
                      <li key={bIdx} className="flex items-start gap-2">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-lotus-deep/80" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Suitable For */}
                <div className="mt-6 rounded-xl bg-lotus-petal/30 p-3.5 text-xs leading-relaxed text-lotus-ink/80">
                  <div className="flex items-center gap-1.5 font-medium text-lotus-deep mb-1">
                    <User size={14} />
                    <span>Who it is suitable for:</span>
                  </div>
                  {pkg.suitableFor}
                </div>
              </div>

              {/* CTA Section */}
              <div className="mt-8 pt-4 border-t border-lotus-petal/60">
                <motion.a
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.95 }}
                  href={getWaLink(pkg.message)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group flex items-center justify-center gap-2.5 w-full rounded-full py-3.5 px-5 text-sm font-semibold transition-all duration-300 ${
                    pkg.popular
                      ? 'bg-lotus-deep text-white hover:bg-lotus-deep/90 shadow-md hover:shadow-lg'
                      : 'bg-white border-2 border-lotus-deep text-lotus-deep hover:bg-lotus-deep hover:text-white shadow-sm'
                  }`}
                >
                  <MessageCircle size={18} className="transition-transform group-hover:scale-110" />
                  <span>Book Consultation via WhatsApp</span>
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </motion.a>
                <p className="mt-2 text-center text-[11px] text-lotus-ink/50">
                  Click to chat directly with default inquiry message
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default IndividualWellness;
